import { useMutation } from '@tanstack/react-query'
import { ApiQueryKeys, CrmTools } from 'globals/constants'
import { AnyObject } from 'globals/typings'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { CrmContactImportModel, CrmContactsApi, ShowAlert } from 'sdk'
import { useAppDispatch } from 'store/app.store'
import { setCrmTool } from 'store/slices'
import { CommonSliceSelector, SetStates } from 'store/slices/common.slice'
import * as XLSX from 'xlsx'

export type MappingKeyType = { Id: string; Title: string; isRequired: boolean }
export type fieldColumnMapping = { field: MappingKeyType; column: string | null }
export type fieldsMapping = Array<fieldColumnMapping>
type processedContact = CrmContactImportModel & { ActualRecord?: AnyObject }
export type fieldMapping = {
  contacts: fieldsMapping
  companies: fieldsMapping
  contactTypeColumn: string
  contactType: string
  groupTagColumn: string
}
export const useImportCrmContact = () => {
  const crmContactApi = new CrmContactsApi()
  const initialMappings: fieldMapping = {
    contacts: [],
    companies: [],
    contactTypeColumn: '',
    contactType: '',
    groupTagColumn: '',
  }
  const [fieldMappings, setFieldMappings] = useState<fieldMapping>(initialMappings)
  const [file, setFile] = useState<File>()
  const [rawImportedContacts, setRawImportedContacts] = useState<Array<any>>([])
  const [isCompanyNotIncluded, setIsCompanyNotIncluded] = useState<boolean>(false)
  const [goodRecords, setGoodRecords] = useState<Array<CrmContactImportModel>>([])
  const [badRecords, setBadRecords] = useState<Array<AnyObject & { Reason: string }>>([])
  const { States } = useSelector(CommonSliceSelector)
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useAppDispatch()
  const [activeStep, setActiveStep] = useState(0)
  const steps = ['Upload File', 'Contact Type', 'Mapping', 'Summary']

  const contactKeys: MappingKeyType[] = [
    { Id: 'ContactEmail', isRequired: true, Title: 'Email' },
    { Id: 'ContactFirstName', isRequired: false, Title: 'First Name' },
    { Id: 'ContactLastName', isRequired: false, Title: 'Last Name' },
    { Id: 'ContactPhone', isRequired: false, Title: 'Phone' },
    { Id: 'ContactAltPhone', isRequired: false, Title: 'Alt Phone' },
    { Id: 'ContactAddress', isRequired: false, Title: 'Address' },
    { Id: 'ContactAddress2', isRequired: false, Title: 'Address2' },
    { Id: 'ContactCity', isRequired: false, Title: 'City' },
    { Id: 'ContactStateId', isRequired: false, Title: 'State' },
    { Id: 'ContactZip', isRequired: false, Title: 'Zip' },
  ]

  const companyKeys: MappingKeyType[] = [
    { Id: 'Name', Title: 'Company Name', isRequired: true },
    { Id: 'Address', Title: 'Address', isRequired: false },
    { Id: 'Address2', Title: 'Address2', isRequired: false },
    { Id: 'City', Title: 'City', isRequired: false },
    { Id: 'State', Title: 'State', isRequired: false },
    { Id: 'Zip', Title: 'Zip', isRequired: false },
    { Id: 'PhoneNumber', Title: 'Phone', isRequired: false },
    { Id: 'AltPhone', Title: 'Alt Phone', isRequired: false },
    { Id: 'Website', Title: 'Website', isRequired: false },
  ]

  const onFileSelect = (_file: File) => {
    setFile(_file)
    const reader = new FileReader()
    reader.onload = (e: any) => {
      const bstr = e.target.result
      const wb = XLSX.read(bstr, { type: 'binary' })
      const wsname = wb.SheetNames[0]
      const ws = wb.Sheets[wsname]
      const data = XLSX.utils.sheet_to_json(ws, { raw: true })
      if (data) {
        setRawImportedContacts(data)
      }
    }
    reader.readAsBinaryString(_file!)
  }
  const fileHeaders = useMemo(() => {
    const keys: any = []
    for (const record of rawImportedContacts) {
      for (const key of Object.keys(record)) {
        if (!keys.includes(key)) {
          keys.push(key)
        }
      }
    }
    return keys
  }, [file, rawImportedContacts])

  const uniqueCompanies = useMemo(() => {
    return Array.from(
      new Set<string | undefined>(
        goodRecords.flatMap((contact) =>
          (contact.Companies || []).map((company) => company.Name).filter((name) => !!name)
        )
      )
    ).filter((name): name is string => !!name)
  }, [goodRecords])

  const init = () => {
    setFieldMappings({ contacts: [], companies: [], contactType: '', contactTypeColumn: '', groupTagColumn: '' })
    for (const key of companyKeys) {
      setFieldMappings((prev) => ({ ...prev, companies: [...prev.companies, { field: key, column: null }] }))
    }
    for (const key of contactKeys) {
      setFieldMappings((prev) => ({ ...prev, contacts: [...prev.contacts, { field: key, column: null }] }))
    }
    SetStates(null)
    // this.isFileUploaded = false
    // this.isCompanyNotIncluded = false
    // this.isContactNotIncluded = false
  }

  const isContactTypeSelected = useCallback(() => {
    return !!fieldMappings.contactTypeColumn || !!fieldMappings.contactType
  }, [fieldMappings])

  const nextButtonText = () => {
    switch (activeStep) {
      case 0:
        return 'Upload'
      case 1:
        return 'Next'
      case 2:
        return 'Next'
      case 3:
        return 'Close'
    }
  }
  const stepOneCompleted = () => {
    return !!file
  }
  const stepTwoCompleted = () => {
    return isContactTypeSelected()
  }
  const stepThreeCompleted = () => {
    return (
      [
        ...fieldMappings.contacts.filter((x) => !!x.field.isRequired && !!!x.column),
        ...(!isCompanyNotIncluded ? fieldMappings.companies.filter((x) => !!x.field.isRequired && !!!x.column) : []),
      ].length === 0
    )
  }

  const isNextDisabled = () => {
    if (activeStep === 0) {
      return !stepOneCompleted()
    } else if (activeStep === 1) {
      return !stepTwoCompleted()
    } else if (activeStep === 2) {
      return !stepThreeCompleted()
    }
  }
  const nextClick = () => {
    if (activeStep === 2) {
      processImportedContacts()
    }
    setActiveStep(activeStep + 1)
  }
  const backClick = () => {
    if (activeStep == 0) {
      dispatch(setCrmTool(CrmTools.Contacts))
      return
    }
    setActiveStep(activeStep - 1)
  }
  const cancelClick = () => {
    ShowAlert('question', 'All unsaved changes will be discarded.<br/>Do you wish to continue?').then((x) => {
      if (x.isConfirmed) {
        dispatch(setCrmTool(CrmTools.Contacts))
        setFile(undefined)
      }
    })
  }

  const isEmailValid = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }
  // public importContact() {
  //     new CrmContactsApi().importContact(this.goodContacts).then(x => {
  //         this.init();
  //     });
  // }

  const { mutate: uploadContacts, isPending: isUploading } = useMutation({
    mutationKey: [ApiQueryKeys.crm.contacts.ImportContacts],
    mutationFn: () => crmContactApi.importContact(goodRecords),
    onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
    onSuccess: () => {
      dispatch(setCrmTool(CrmTools.Contacts))
      enqueueSnackbar(
        'We’ve started importing your records in the background while you continue to work. Please check your Contacts CRM records in a few minutes.',
        { variant: 'success', autoHideDuration: 5000 }
      )
    },
  })
  const processImportedContacts = () => {
    setGoodRecords([])
    setBadRecords([])

    const goodContacts: Array<CrmContactImportModel> = []
    const badContacts: Array<AnyObject & { Reason: string }> = []
    const contacts = rawImportedContacts.map((x) => {
      const contact: processedContact = { ActualRecord: x } as any
      for (const key of fieldMappings.contacts) {
        ;(contact as any)[key.field.Id] = x[key.column!]
      }
      if (fieldMappings.contactTypeColumn) {
        contact.ContactType = x[fieldMappings.contactTypeColumn]
      } else {
        contact.ContactType = fieldMappings.contactType
      }
      const company: any = {}
      for (const key of fieldMappings.companies) {
        company[key.field.Id] = x[key.column!]
      }
      if (company.Name) {
        contact.Companies = [company]
      }
      if (
        fieldMappings.groupTagColumn &&
        x[fieldMappings.groupTagColumn]?.split(',').length === 1 &&
        x[fieldMappings.groupTagColumn]?.split('|').length === 1
      ) {
        contact.GroupTags = [x[fieldMappings.groupTagColumn]]
      }
      return contact
    })

    for (const contact of contacts) {
      const oldRec = goodContacts?.find((x) => x.ContactEmail === contact.ContactEmail)
      if (oldRec) {
        const isSameCompany = oldRec.Companies?.find((x) => x.Name === contact.Companies?.[0].Name)
        const isSameGroupTag = oldRec.GroupTags?.find((x) => x === contact.GroupTags?.[0])
        if (isSameCompany && isSameGroupTag) {
          badContacts?.push({ Reason: 'Duplicate', ...contact.ActualRecord })
        } else if (!isSameCompany) {
          if (contact.Companies && contact.Companies.length > 0) {
            if (!!contact.Companies[0].State) {
              const stateId = States.find(
                (x) =>
                  x.Code?.toLocaleLowerCase() === contact.Companies![0].State?.toLocaleLowerCase() ||
                  x.Name?.toLocaleLowerCase() === contact.Companies![0].State?.toLocaleLowerCase()
              )
              if (stateId?.Id) {
                oldRec.Companies = [...(oldRec.Companies ?? []), ...(contact.Companies ?? [])]
              } else {
                badContacts.push({ Reason: 'Invalid Company State', ...contact.ActualRecord })
                continue
              }
            }
          }
        } else if (!isSameGroupTag) {
          oldRec.GroupTags = [...(oldRec.GroupTags ?? []), ...(contact.GroupTags ?? [])]
        }
      } else {
        if (!contact.ContactEmail) {
          badContacts.push({ Reason: 'No Email', ...contact.ActualRecord })
          continue
        }
        if (!isEmailValid(contact.ContactEmail)) {
          badContacts.push({ Reason: 'Invalid Email', ...contact.ActualRecord })
          continue
        }
        if (!contact.ContactType) {
          badContacts.push({ Reason: 'No Contact Type', ...contact.ActualRecord })
          continue
        }
        if (!!contact.ContactStateId) {
          const stateId = States.find(
            (x) =>
              x.Code?.toLocaleLowerCase() === contact.ContactStateId?.toLocaleLowerCase() ||
              x.Name?.toLocaleLowerCase() === contact.ContactStateId?.toLocaleLowerCase()
          )
          if (stateId?.Id) {
            contact.ContactStateId = stateId.Id
          } else {
            badContacts.push({ Reason: 'Invalid State', ...contact.ActualRecord })
            continue
          }
        }
        if (contact.Companies && contact.Companies.length > 0) {
          if (!!contact.Companies[0].State) {
            const stateId = States.find(
              (x) =>
                x.Code?.toLocaleLowerCase() === contact.Companies![0].State?.toLocaleLowerCase() ||
                x.Name?.toLocaleLowerCase() === contact.Companies![0].State?.toLocaleLowerCase()
            )
            if (stateId?.Id) {
              contact.Companies[0].State = stateId.Id
            } else {
              badContacts.push({ Reason: 'Invalid Company State', ...contact.ActualRecord })
              continue
            }
          }
          if (!!contact.Companies[0].Phone) {
            contact.Companies[0].Phone = contact.Companies[0].Phone + ''
            contact.Companies[0].Phone = contact.Companies[0].Phone?.replace(/\W+/g, '')!
            if (contact.Companies[0].Phone[0] === '1') {
              contact.Companies[0].Phone = contact.Companies[0].Phone.slice(1)
            }
            if (contact.Companies[0].Phone.length !== 10) {
              badContacts.push({ Reason: 'Invalid Company Phone', ...contact.ActualRecord })
              continue
            }
            contact.Companies[0].Phone = contact.Companies[0].Phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
          }
          if (!!contact.Companies[0].AltPhone && contact.Companies[0].AltPhone.length > 0) {
            contact.Companies[0].AltPhone = contact.Companies[0].AltPhone + ''
            contact.Companies[0].AltPhone = contact.Companies[0].AltPhone?.replace(/\W+/g, '')!
            if (contact.Companies[0].AltPhone[0] === '1') {
              contact.Companies[0].AltPhone = contact.Companies[0].AltPhone.slice(1)
            }
            if (contact.Companies[0].AltPhone.length !== 10) {
              badContacts.push({ Reason: 'Invalid Company Alt Phone', ...contact.ActualRecord })
              continue
            }
            contact.Companies[0].AltPhone = contact.Companies[0].AltPhone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
          }
          if (!!contact.Companies[0].Zip) {
            contact.Companies[0].Zip = contact.Companies[0].Zip + ''
            contact.Companies[0].Zip = contact.Companies[0].Zip.replace(/\D+/g, '') // Remove non-numeric characters

            if (contact.Companies[0].Zip.length === 9) {
              contact.Companies[0].Zip = contact.Companies[0].Zip.slice(0, 5) // Truncate to 5 digits
            }

            if (contact.Companies[0].Zip.length !== 5) {
              badContacts.push({ Reason: 'Invalid Company ZIP Code', ...contact.ActualRecord })
              continue
            }
          }
          if (!!contact.Companies[0].Website) {
            if (
              !contact.Companies[0].Website.startsWith('http://') &&
              !contact.Companies[0].Website.startsWith('https://')
            ) {
              contact.Companies[0].Website = 'https://' + contact.Companies[0].Website
            }
          }
        }
        if (!!contact.ContactPhone) {
          contact.ContactPhone = contact.ContactPhone + ''
          contact.ContactPhone = contact.ContactPhone.replace(/\W+/g, '')!
          if (contact.ContactPhone[0] == '1') {
            contact.ContactPhone = contact.ContactPhone.slice(1)
          }
          if (contact.ContactPhone.length !== 10) {
            badContacts.push({ Reason: 'Invalid Phone', ...contact.ActualRecord })
            continue
          }
          contact.ContactPhone = contact.ContactPhone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
        }
        if (!!contact.ContactZip) {
          contact.ContactZip = contact.ContactZip + ''
          contact.ContactZip = contact.ContactZip.replace(/\D+/g, '') // Remove non-numeric characters

          if (contact.ContactZip.length === 9) {
            contact.ContactZip = contact.ContactZip.slice(0, 5) // Truncate to 5 digits
          }

          if (contact.ContactZip.length !== 5) {
            badContacts.push({ Reason: 'Invalid ZIP Code', ...contact.ActualRecord })
            continue
          }
        }
        if (!!contact.ContactAltPhone && contact.ContactAltPhone.length > 0) {
          contact.ContactAltPhone = contact.ContactAltPhone + ''
          contact.ContactAltPhone = contact.ContactAltPhone.replace(/\W+/g, '')!
          if (contact.ContactAltPhone[0] == '1') {
            contact.ContactAltPhone = contact.ContactAltPhone.slice(1)
          }
          if (contact.ContactAltPhone.length !== 10) {
            badContacts.push({ Reason: 'Invalid Alt Phone', ...contact.ActualRecord })
            continue
          }
          contact.ContactAltPhone = contact.ContactAltPhone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
        }
        delete contact.ActualRecord
        goodContacts.push(contact)
      }
    }
    setGoodRecords(goodContacts)
    setBadRecords(badContacts)
    console.log(goodContacts)
  }

  useEffect(() => init(), [])
  return {
    steps,
    activeStep,
    fieldMappings,
    fileHeaders,
    contactKeys,
    companyKeys,
    isCompanyNotIncluded,
    rawImportedContacts,
    goodRecords,
    badRecords,
    uniqueCompanies,
    isUploading,
    uploadContacts,
    nextButtonText,
    isNextDisabled,
    nextClick,
    backClick,
    cancelClick,
    onFileSelect,
    setFieldMappings,
    setIsCompanyNotIncluded,
  }
}
